import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { t } from '@/lib/ui'
import { PRIMARY_AREAS, SECONDARY_AREAS, type Area } from '@/lib/site'

// Top navigation, part of the persistent shell. Primary = the four large FI
// areas; secondary = the four small EN areas, visually subordinate (muted,
// smaller) per Kimmo's brief. Collapses to a menu below lg.

const primaryLink =
  'inline-flex items-center border-b-2 border-transparent pb-0.5 text-sm font-medium text-ink transition-colors hover:text-accent aria-[current=page]:border-accent aria-[current=page]:text-accent'
const secondaryLink =
  'inline-flex items-center border-b-2 border-transparent pb-0.5 text-sm text-muted transition-colors hover:text-accent aria-[current=page]:border-accent aria-[current=page]:text-accent'

function Wordmark() {
  return (
    <Link to="/" className="group inline-flex items-baseline gap-2" aria-label={t('site.name')}>
      <span className="font-semibold tracking-tight text-ink">{t('site.name')}</span>
      <span className="hidden text-xs text-muted sm:inline">{t('site.tagline')}</span>
    </Link>
  )
}

export function SiteNav() {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  // Escape closes the mobile menu.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
        menuButtonRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <nav className="border-b border-hairline bg-paper" aria-label={t('site.name')}>
      <div className="mx-auto flex h-14 max-w-shell items-center justify-between px-4">
        <Wordmark />

        {/* Desktop nav */}
        <div className="hidden items-center gap-5 lg:flex">
          {PRIMARY_AREAS.map((a) => (
            <NavLink key={a.slug} to={a.slug} className={primaryLink}>
              {t(a.labelKey)}
            </NavLink>
          ))}
          <span className="h-5 w-px bg-hairline" aria-hidden="true" />
          {SECONDARY_AREAS.map((a) => (
            <NavLink key={a.slug} to={a.slug} className={secondaryLink}>
              {t(a.labelKey)}
            </NavLink>
          ))}
        </div>

        {/* Mobile toggle */}
        <button
          ref={menuButtonRef}
          type="button"
          className="inline-flex items-center rounded p-1.5 text-ink hover:bg-accent-soft lg:hidden"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? t('nav.closeMenu') : t('nav.openMenu')}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu panel */}
      {open && (
        <div id="mobile-menu" className="border-t border-hairline bg-paper lg:hidden">
          <ul className="mx-auto max-w-shell divide-y divide-hairline px-4">
            {PRIMARY_AREAS.map((a) => (
              <MobileItem key={a.slug} area={a} />
            ))}
            {SECONDARY_AREAS.map((a) => (
              <MobileItem key={a.slug} area={a} muted />
            ))}
          </ul>
        </div>
      )}
    </nav>
  )
}

function MobileItem({ area, muted = false }: { area: Area; muted?: boolean }) {
  const Icon = area.icon
  return (
    <li>
      <NavLink
        to={area.slug}
        className={`flex items-center gap-3 py-3 text-base transition-colors hover:text-accent aria-[current=page]:font-medium aria-[current=page]:text-accent ${
          muted ? 'text-muted' : 'text-ink'
        }`}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t(area.labelKey)}
        {muted && <span className="text-xs text-muted">{t('common.langNoteEn')}</span>}
      </NavLink>
    </li>
  )
}
